import { zodResolver } from '@hookform/resolvers/zod';
import {
  SALE_DISCOUNT_REASON_LABELS,
  SALE_DISCOUNT_REASONS,
  type SaleDiscount,
} from '@repo/desktop-bridge';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { type DefaultValues, useForm } from 'react-hook-form';
import { z } from 'zod';

import { formatPaise, paiseFromRupees, RUPEES_SHAPE, rupeesFromPaise } from '@/lib/money';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';

/**
 * A whole-bill discount at the till (#352): an amount off, and why — **picked
 * from four, never typed**, so the report groups cleanly instead of carrying
 * twenty-five spellings of "damaged".
 *
 * **The cap is refused here, and this is the refusal a guest sees.** The server
 * records a sale over the cap rather than refusing it after the cash is in the
 * drawer, so the till holds the line itself, from the cap on its held
 * catalogue — offline included. `mostOffPaise` is that cap worked out against
 * this bill; the main process checks it again before anything is spent.
 */

/** Where the amount is refused, the sentence says how much this bill allows. */
const overCapMessage = (mostOffPaise: number): string =>
  mostOffPaise === 0
    ? 'No discount can be given on this bill.'
    : `At most ${formatPaise(mostOffPaise)} off this bill.`;

const schemaFor = (mostOffPaise: number) =>
  z.object({
    amount: z
      .string()
      .trim()
      .min(1, 'Enter the amount off, in rupees.')
      .regex(RUPEES_SHAPE, 'An amount is rupees and paise — 25 or 25.50, with no symbol.')
      .refine((rupees) => paiseFromRupees(rupees) > 0, 'Enter an amount above nothing.')
      .refine((rupees) => paiseFromRupees(rupees) <= mostOffPaise, overCapMessage(mostOffPaise)),
    reason: z.enum(SALE_DISCOUNT_REASONS, { message: 'Choose why.' }),
  });

export function DiscountDialog({
  current,
  mostOffPaise,
  onApply,
  onRemove,
  onClose,
}: {
  current: SaleDiscount | null;
  /** The most this bill may have taken off it, under the park's cap. */
  mostOffPaise: number;
  onApply: (discount: SaleDiscount) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const schema = schemaFor(mostOffPaise);
  // Partial on purpose: a new discount has no reason until one is picked.
  const defaultValues: DefaultValues<z.input<typeof schema>> = {
    amount: current === null ? '' : rupeesFromPaise(current.amountPaise),
    ...(current === null ? {} : { reason: current.reason }),
  };
  const form = useForm<z.input<typeof schema>, unknown, z.output<typeof schema>>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues,
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <FormDialogContent>
        <DialogHeader>
          <DialogTitle>Discount on this bill</DialogTitle>
          <DialogDescription className="numeric">
            {mostOffPaise === 0
              ? 'No discount can be given on this bill.'
              : `Up to ${formatPaise(mostOffPaise)} on this bill.`}
          </DialogDescription>
        </DialogHeader>

        {/* Nothing may come off — a till that has not heard the cap, or a cap of
            nothing — so there is nothing to fill in, only the sentence above. */}
        {mostOffPaise === 0 && current === null ? (
          <DialogFooter>
            <Button type="button" variant="outline" size="touch" onClick={onClose}>
              Close
            </Button>
          </DialogFooter>
        ) : (
          <Form {...form}>
            <form
              className="space-y-5"
              noValidate
              onSubmit={(event) => {
                // A portal is still inside the sale's React tree, and a submit
                // that bubbled out of here would take payment.
                event.stopPropagation();
                void form.handleSubmit(({ amount, reason }) => {
                  onApply({ amountPaise: paiseFromRupees(amount), reason });
                })(event);
              }}
            >
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required className={fieldLabel}>
                      Amount off, ₹
                    </FormLabel>
                    <FormControl
                      render={
                        <Input
                          className={`${fieldSurface} h-touch font-mono text-lg numeric md:text-lg`}
                          required
                          inputMode="decimal"
                          placeholder="25.00"
                          autoComplete="off"
                          {...field}
                        />
                      }
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    {/* A named span rather than `FormLabel`, as the payment modes
                      do: a radio group has no single control for a label to point
                      at. */}
                    <span id="discount-reason-label" className={fieldLabel}>
                      Why
                      <span aria-hidden className="text-destructive">
                        *
                      </span>
                    </span>
                    <div
                      role="radiogroup"
                      aria-labelledby="discount-reason-label"
                      aria-required
                      className="grid grid-cols-2 gap-2"
                    >
                      {SALE_DISCOUNT_REASONS.map((reason) => (
                        <Button
                          key={reason}
                          type="button"
                          role="radio"
                          aria-checked={field.value === reason}
                          variant={field.value === reason ? 'secondary' : 'outline'}
                          size="touch"
                          onClick={() => {
                            field.onChange(reason);
                          }}
                        >
                          {SALE_DISCOUNT_REASON_LABELS[reason]}
                        </Button>
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                {current !== null && (
                  <Button type="button" variant="outline" size="touch" onClick={onRemove}>
                    Remove discount
                  </Button>
                )}
                <Button type="submit" size="touch">
                  Apply discount
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </FormDialogContent>
    </Dialog>
  );
}
